import { Copy, Link2, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import {
  createInvite,
  inviteLink,
  InvitesApiError,
  listInvites,
  revokeInvite,
} from '../api/invites';
import type { CreatedInvite, RoomInvite } from '../api/invites';
import { copyText } from './clipboard';

/** Expiry presets of the create row: hours, or never (absent on the wire). */
const EXPIRY_CHOICES: readonly { readonly value: string; readonly hours: number | null }[] = [
  { value: '1h', hours: 1 },
  { value: '24h', hours: 24 },
  { value: '7d', hours: 24 * 7 },
  { value: 'never', hours: null },
];

/** Server-side use-quota bound (invites.go inviteMaxUses); the input clamps to it. */
const MAX_USES_LIMIT = 10000;

/** How long a copy confirmation stays visible on the button (InviteDialog idiom). */
const COPIED_CONFIRMATION_MS = 2000;

export interface InvitesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The room slug; both the API path segment and the deep-link's /r/<slug> part. */
  readonly room: string;
  /** Current join JWT for the Bearer calls; null while no token is held (fetches skip). */
  readonly getToken: () => string | null;
}

type LoadState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'error' };

/**
 * The staff's invitation-links dialog (M4 item 4, CR-20): a create row (expiry preset, optional
 * use quota), the minted invitation's full deep link shown ONCE with a copy button (the server
 * stores only the token's hash, so this is the only chance to pass it on), and the management
 * list (created by/when, uses, expiry, revoked state) with a two-step revoke per row. Modal on
 * the InviteDialog/EffectsDialog idiom; the Bearer is the live room JWT (RecordingsPanel's
 * getToken idiom), refreshed on every open.
 */
export function InvitesDialog(props: InvitesDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [invites, setInvites] = useState<readonly RoomInvite[] | null>(null);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const [expiry, setExpiry] = useState<string>('24h');
  const [maxUses, setMaxUses] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [busyRevokingId, setBusyRevokingId] = useState<number | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  // A refresh that lost the race against a newer one must not overwrite the fresher list.
  const refreshSeqRef = useRef(0);
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = props.getToken();

  const refresh = useCallback(async (): Promise<void> => {
    const token = tokenRef.current;
    if (token === null) {
      return;
    }
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    setLoadState({ kind: 'loading' });
    try {
      const list = await listInvites(props.room, token);
      if (refreshSeqRef.current === seq) {
        setInvites(list);
        setLoadState({ kind: 'ready' });
      }
    } catch {
      if (refreshSeqRef.current === seq) {
        setLoadState({ kind: 'error' });
      }
    }
  }, [props.room]);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    // Every open re-arms the form and re-fetches: a minted one-time link from the previous
    // open must never linger in the dialog.
    setCreated(null);
    setCreateError(null);
    setCopied(false);
    setRevokingId(null);
    setMaxUses('');
    setExpiry('24h');
    void refresh();
  }, [props.open, refresh]);

  useEffect(
    () => () => {
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
      }
    },
    [],
  );

  async function handleCreate(): Promise<void> {
    const token = tokenRef.current;
    if (token === null || creating) {
      return;
    }
    const choice = EXPIRY_CHOICES.find((candidate) => candidate.value === expiry);
    const trimmedUses = maxUses.trim();
    let parsedUses: number | undefined;
    if (trimmedUses.length > 0) {
      const parsed = Number(trimmedUses);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_USES_LIMIT) {
        setCreateError(t('invites.max_uses_invalid'));
        return;
      }
      parsedUses = parsed;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const minted = await createInvite(
        props.room,
        token,
        {
          ...(choice?.hours !== null && choice !== undefined
            ? { expiresAt: new Date(Date.now() + choice.hours * 3_600_000).toISOString() }
            : {}),
          ...(parsedUses !== undefined ? { maxUses: parsedUses } : {}),
        },
      );
      setCreated(minted);
      // Prepend so the fresh invitation is visible without a round trip; the next refresh
      // converges on the server order (newest first).
      setInvites((current) =>
        current === null
          ? current
          : [
              {
                id: minted.id,
                createdBy: '',
                expiresAt: minted.expiresAt,
                maxUses: minted.maxUses,
                usedCount: 0,
                revokedAt: null,
                createdAt: minted.createdAt,
              },
              ...current.filter((candidate) => candidate.id !== minted.id),
            ],
      );
    } catch (err: unknown) {
      setCreateError(t('invites.create_failed'));
      if (err instanceof InvitesApiError && err.status === 400) {
        setCreateError(t('invites.max_uses_invalid'));
      }
    } finally {
      setCreating(false);
    }
  }

  async function copyCreatedLink(): Promise<void> {
    if (created === null) {
      return;
    }
    const ok = await copyText(inviteLink(window.location.origin, props.room, created.token));
    setCopied(ok);
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current);
    }
    copyTimerRef.current = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_CONFIRMATION_MS);
  }

  async function handleRevoke(id: number): Promise<void> {
    const token = tokenRef.current;
    if (token === null || busyRevokingId !== null) {
      return;
    }
    setBusyRevokingId(id);
    try {
      await revokeInvite(props.room, token, id);
      setInvites((current) =>
        current === null
          ? current
          : current.map((candidate) =>
              candidate.id === id && candidate.revokedAt === null
                ? { ...candidate, revokedAt: new Date().toISOString() }
                : candidate,
            ),
      );
    } catch {
      // Optimistic stamp rolled back by the next refresh; surface the state via reload.
      await refresh();
    } finally {
      setRevokingId(null);
      setBusyRevokingId(null);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-testid="invites-dialog"
        className="border-white/10 bg-zinc-900 text-zinc-100 sm:max-w-[520px]"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">
            {t('invites.title')}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400">
            {t('invites.description')}
          </DialogDescription>
        </DialogHeader>

        {/* create row */}
        <div className="space-y-2.5 rounded-lg border border-white/[0.08] bg-white/[0.03] p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                {t('invites.expires_label')}
              </span>
              <select
                data-testid="invites-expiry"
                value={expiry}
                onChange={(event) => {
                  setExpiry(event.target.value);
                }}
                className="h-9 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 text-[12.5px] text-zinc-100 outline-none focus:border-white/20 dark:[color-scheme:dark]"
              >
                {EXPIRY_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {t(`invites.expires_${choice.value}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex w-28 flex-col gap-1">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                {t('invites.max_uses_label')}
              </span>
              <input
                data-testid="invites-max-uses"
                type="number"
                min={1}
                max={MAX_USES_LIMIT}
                value={maxUses}
                onChange={(event) => {
                  setMaxUses(event.target.value);
                  setCreateError(null);
                }}
                placeholder={t('invites.max_uses_placeholder')}
                className="h-9 w-full rounded-lg border border-white/10 bg-white/[0.05] px-2 text-[12.5px] text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-white/20"
              />
            </label>
            <button
              type="button"
              data-testid="invites-create"
              disabled={creating || loadState.kind === 'loading'}
              onClick={() => {
                void handleCreate();
              }}
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-semibold text-zinc-950 transition-colors hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {creating ? (
                <Loader2 aria-hidden className="size-3.5 animate-spin" />
              ) : (
                <Plus aria-hidden className="size-3.5" />
              )}
              {t('invites.create')}
            </button>
          </div>
          {createError !== null ? (
            <p role="alert" data-testid="invites-create-error" className="text-[11.5px] text-red-300">
              {createError}
            </p>
          ) : null}
          {created !== null ? (
            <div
              data-testid="invites-created"
              className="space-y-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] p-2.5"
            >
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
                {t('invites.link_label')}
              </p>
              <div className="flex gap-2">
                <input
                  readOnly
                  data-testid="invite-link"
                  value={inviteLink(window.location.origin, props.room, created.token)}
                  aria-label={t('invites.link_aria')}
                  onFocus={(event) => {
                    event.currentTarget.select();
                  }}
                  className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.05] px-3 font-mono text-[11.5px] text-zinc-200 outline-none focus-visible:border-white/20"
                />
                <button
                  type="button"
                  data-testid="invite-link-copy"
                  onClick={() => {
                    void copyCreatedLink();
                  }}
                  className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-[12px] font-semibold text-zinc-950 transition-colors hover:bg-zinc-200"
                >
                  <Copy aria-hidden className="size-3.5" />
                  {copied ? t('invites.copied') : t('invites.copy')}
                </button>
              </div>
              <p className="text-[11px] leading-snug text-amber-300/90">
                {t('invites.link_one_time_note')}
              </p>
            </div>
          ) : null}
        </div>

        {/* management list */}
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
            <Link2 aria-hidden className="size-3" />
            {t('invites.list_title')}
          </p>
          <button
            type="button"
            data-testid="invites-refresh"
            aria-label={t('invites.refresh_aria')}
            title={t('invites.refresh_aria')}
            onClick={() => {
              void refresh();
            }}
            className="grid size-7 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <RefreshCw aria-hidden className="size-3.5" />
          </button>
        </div>
        <div
          data-testid="invites-list"
          className="max-h-64 space-y-1.5 overflow-y-auto scroll-thin-dark"
          aria-label={t('invites.list_aria')}
        >
          {loadState.kind === 'error' ? (
            <p role="alert" data-testid="invites-error" className="px-1 py-3 text-xs text-red-300">
              {t('invites.list_failed')}
            </p>
          ) : null}
          {loadState.kind === 'loading' && invites === null ? (
            <p className="px-1 py-3 text-xs text-zinc-500">{t('invites.list_loading')}</p>
          ) : null}
          {loadState.kind === 'ready' && invites !== null && invites.length === 0 ? (
            <p className="px-1 py-3 text-center text-xs text-zinc-500">
              {t('invites.list_empty')}
            </p>
          ) : null}
          {(invites ?? []).map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              confirming={revokingId === invite.id}
              busy={busyRevokingId === invite.id}
              onAskRevoke={() => {
                setRevokingId(invite.id);
              }}
              onCancelRevoke={() => {
                setRevokingId(null);
              }}
              onRevoke={() => {
                void handleRevoke(invite.id);
              }}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface InviteRowProps {
  readonly invite: RoomInvite;
  readonly confirming: boolean;
  readonly busy: boolean;
  readonly onAskRevoke: () => void;
  readonly onCancelRevoke: () => void;
  readonly onRevoke: () => void;
}

/** One management row: meta line (by/when, uses, expiry) plus the two-step revoke. */
function InviteRow(props: InviteRowProps): JSX.Element {
  const { t } = useTranslation();
  const { invite } = props;
  const revoked = invite.revokedAt !== null;
  const exhausted = invite.maxUses > 0 && invite.usedCount >= invite.maxUses;
  return (
    <div
      data-testid={`invite-row-${String(invite.id)}`}
      className={cn(
        'rounded-lg border px-2.5 py-2',
        revoked
          ? 'border-white/[0.04] bg-white/[0.01] opacity-70'
          : 'border-white/[0.08] bg-white/[0.03]',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] font-semibold text-zinc-300">
          #{String(invite.id)}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-zinc-400">
          {t('invites.created_by', { by: invite.createdBy.length > 0 ? invite.createdBy : '—' })}
        </span>
        {revoked ? (
          <span
            data-testid={`invite-revoked-${String(invite.id)}`}
            className="shrink-0 rounded bg-red-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-red-300"
          >
            {t('invites.revoked_badge')}
          </span>
        ) : exhausted ? (
          <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-amber-300">
            {t('invites.exhausted_badge')}
          </span>
        ) : null}
        {revoked ? null : props.confirming ? (
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              data-testid={`invite-revoke-cancel-${String(invite.id)}`}
              onClick={props.onCancelRevoke}
              className="h-6 rounded-md border border-white/10 px-2 text-[10.5px] text-zinc-300 transition-colors hover:bg-white/10"
            >
              {t('invites.revoke_cancel')}
            </button>
            <button
              type="button"
              data-testid={`invite-revoke-confirm-${String(invite.id)}`}
              disabled={props.busy}
              onClick={props.onRevoke}
              className="flex h-6 items-center gap-1 rounded-md border border-red-400/30 bg-red-400/10 px-2 text-[10.5px] font-medium text-red-300 transition-colors hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {props.busy ? (
                <Loader2 aria-hidden className="size-3 animate-spin" />
              ) : (
                <Trash2 aria-hidden className="size-3" />
              )}
              {t('invites.revoke_confirm')}
            </button>
          </span>
        ) : (
          <button
            type="button"
            data-testid={`invite-revoke-${String(invite.id)}`}
            aria-label={t('invites.revoke_aria', { id: invite.id })}
            onClick={props.onAskRevoke}
            className="grid size-6 shrink-0 place-items-center rounded-md text-zinc-500 transition-colors hover:bg-red-400/10 hover:text-red-300"
          >
            <Trash2 aria-hidden className="size-3" />
          </button>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] tabular-nums text-zinc-500">
        <span data-testid={`invite-uses-${String(invite.id)}`}>
          {t('invites.uses', {
            used: invite.usedCount,
            max:
              invite.maxUses > 0
                ? String(invite.maxUses)
                : t('invites.uses_unlimited'),
          })}
        </span>
        <span data-testid={`invite-expires-${String(invite.id)}`}>
          {invite.expiresAt !== null
            ? t('invites.expires_at', {
                at: formatInviteTime(invite.expiresAt),
              })
            : t('invites.expires_never')}
        </span>
      </div>
    </div>
  );
}

/** Localized wall-clock label (date + short time); falls back to the raw string. */
function formatInviteTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return iso;
  }
}
